'use client';

import React, { useEffect, useRef, type ReactNode } from 'react';
import { cssEasing, type AnimationKeyframe, type AnimationSpec } from './animation';
import type { AnimateProps } from './primitives.types';
import { webStyle } from './primitives.web';

/*
  Plays a spec once on a child that is already on screen, and leaves it where it started.

  `Presence` is the other half of this: it animates something *into* or *out of* existence, and the
  child's mounting is what the animation is about. Here nothing appears or disappears — a solved
  Fields board lifts each field in turn and sets it back down — so the child is always mounted and
  `play` only says when the motion runs.

  Nothing plays until `play` turns true, and a spec's own `delay` is what staggers one element
  against another.
*/

const transformOf = (frame: AnimationKeyframe): string =>
  `translateX(${frame.translateX ?? 0}px) translateY(${frame.translateY ?? 0}px) scaleX(${frame.scaleX ?? 1}) scaleY(${frame.scaleY ?? 1}) rotate(${frame.rotate ?? 0}deg)`;

/**
 * Whether a frame asks for no transform at all, in which case none is written.
 *
 * An identity transform is not free: it puts the element on its own compositing layer, and a subtree
 * rasterised on one lands on subtly different pixels from the same subtree drawn in place. A spec
 * that only fades has no business moving anything, least of all by a fraction of a pixel.
 */
const isStill = (frame: AnimationKeyframe): boolean =>
  (frame.translateX ?? 0) === 0 &&
  (frame.translateY ?? 0) === 0 &&
  (frame.scaleX ?? 1) === 1 &&
  (frame.scaleY ?? 1) === 1 &&
  (frame.rotate ?? 0) === 0;

const keyframesOf = (spec: AnimationSpec): Keyframe[] =>
  spec.keyframes.map((frame) => ({
    offset: frame.offset,
    transform: transformOf(frame),
    opacity: frame.opacity ?? 1,
    // On the keyframe, not in the options: see Presence, which explains the same choice.
    easing: cssEasing(spec.easing),
  }));

export const Animate: React.FC<AnimateProps> = ({ play, spec, style, children }) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || !play) return;

    const animation = element.animate(keyframesOf(spec), {
      duration: spec.duration,
      delay: spec.delay ?? 0,
      iterations: spec.iterations === 'infinite' ? Infinity : (spec.iterations ?? 1),
      easing: 'linear',
      // The last frame is the resting one, so holding it is how the element stays put until the
      // delay-staggered siblings have all had their turn.
      fill: 'both',
    });

    return () => animation.cancel();
  }, [play, spec]);

  /*
    Where it sits before it has played, which is the frame it starts from — the same place the native
    half rests, since its progress value starts at zero. Once it has played, WAAPI's `fill: both`
    holds the last frame and overrides this.
  */
  const resting = spec.keyframes[0];

  return (
    <div
      ref={ref}
      style={{
        // React Native's box defaults, as the web Box adopts them, so a `flex: 1` child fills this
        // the way it fills a cell. Without them the child has no height and only what is painted
        // behind it shows — a lifting Fields cell would be a black box.
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        ...webStyle(style),
        // Before the first play, and after a cancel, the element sits at the spec's resting frame.
        opacity: resting.opacity,
        transform: isStill(resting) ? undefined : transformOf(resting),
        transformOrigin: spec.origin === 'bottom' ? 'bottom center' : 'center',
      }}
    >
      {children}
    </div>
  );
};
